"use client";

import { Tooltip, Button } from "@mui/material";
import { Rocket, Upgrade } from "@mui/icons-material";
import Link from "next/link";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { getUserPlans, checkTrialEligibility } from "@/services/APIs/AIUsageService";
import { useAuth } from "@/services/Firebase/Authcontext";

export function UpgradeNowButton() {
	const { currentUser, subscription, subscriptionLoading } = useAuth();
	const [trialEligibility, setTrialEligibility] = useState<{ isEligible: boolean; hasUsedTrialBefore: boolean } | null>(null);
	const [checkingEligibility, setCheckingEligibility] = useState<boolean>(true);

	const isDefaultPlan = subscription?.plan?.isDefault ?? true;

	useEffect(() => {
		const checkEligibility = async () => {
			if (!currentUser || !isDefaultPlan) {
				setCheckingEligibility(false);
				return;
			}

			try {
				// Fetch all plans and find the non-default one (Pro plan)
				const plans = await getUserPlans(false);
				const proPlan = plans.find((plan) => !plan.isDefault && plan.hasTrial);

				if (proPlan) {
					// Check trial eligibility
					const eligibility = await checkTrialEligibility(proPlan.id);
					setTrialEligibility(
						eligibility
							? {
									isEligible: eligibility.isEligible,
									hasUsedTrialBefore: eligibility.hasUsedTrialBefore,
							  }
							: null
					);
				}
			} catch (error) {
				console.error("Failed to check trial eligibility in UpgradeNowButton:", error);
			} finally {
				setCheckingEligibility(false);
			}
		};

		checkEligibility();
	}, [currentUser, isDefaultPlan]);

	if (subscriptionLoading || checkingEligibility) {
		return null;
	}

	const isEligibleForTrial = trialEligibility?.isEligible && !trialEligibility?.hasUsedTrialBefore;
	const buttonText = isEligibleForTrial ? "Start Free Trial" : "Upgrade Now";
	const buttonIcon = isEligibleForTrial ? <Rocket sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }} /> : <Upgrade sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }} />;
	const tooltipTitle = isEligibleForTrial ? "Get a 15-day free trial of Pro features" : "Upgrade to unlock premium features";

	return (
		<motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
			<Tooltip title={tooltipTitle}>
				<Button
					component={Link}
					href="/pricing"
					aria-label={buttonText}
					variant="contained"
					size="small"
					startIcon={buttonIcon}
					sx={{
						textDecoration: "none", // Ensure no underline from Link
						fontWeight: 700,
						fontSize: { xs: "0.65rem", sm: "0.75rem" },
						lineHeight: { xs: 1.2, sm: 1.35 },
						textTransform: "uppercase",
						letterSpacing: "0.5px",
						mr: 1,
						minWidth: { xs: "auto", sm: "auto" },
						height: { xs: 40, sm: "auto" },
						px: { xs: 0.75, sm: 1.25 },
						"& .MuiButton-startIcon": {
							mr: { xs: 0.5, sm: 1 },
						},
						"& .desktop-label": {
							display: "inline",
						},
						background: "linear-gradient(135deg, #536dfe 0%, #7c4dff 100%)",
						color: "white",
						border: "1px solid rgba(255, 255, 255, 0.1)",
						backdropFilter: "blur(10px)",
						boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
						transition: "all 0.3s ease",
						"&:hover": {
							background: "linear-gradient(135deg, #7c4dff 0%, #536dfe 100%)",
							transform: "translateY(-2px)",
							boxShadow: "0 6px 16px rgba(102, 126, 234, 0.4)",
						},
						"&:active": {
							transform: "translateY(0px)",
						},
					}}
				>
					<span className="desktop-label">{buttonText}</span>
				</Button>
			</Tooltip>
		</motion.div>
	);
}
